/**
 * Employment History Form Section
 * Manages current and past employment with collapsed summary cards,
 * multi-income support (bonus/commission/overtime), and store integration.
 */

import * as React from "react";
import {
  DomainBorrowerEmploymentStatus,
  DomainFrequencyType,
  DomainIncomeType,
} from "@porchpass/api-client";
import {
  Add01Icon,
  Alert01Icon,
  Button,
  Delete03Icon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Edit02Icon,
  EmploymentFields,
  FormTable,
  FormTableAddButton,
  FormTableBody,
  MoreVerticalIcon,
  RadioGroup,
  RadioGroupCardItem,
  StarShape,
  MoonShape,
  CircleShape,
  Flower1Shape,
  Flower2Shape,
  useContainerRadiusSync,
  useFormTableAddButtonState,
  WorkHistoryIcon,
} from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type {
  Address,
  Employment,
  EmploymentIncome,
} from "../types/applicationTypes";
import { clearValidEmploymentIncomeFieldErrors } from "../validation/validateApplication";
import {
  validateAddressField,
  validateEmploymentField,
} from "../utils/validationSchemas";
import { formatDateRange } from "../constants/dateOptions";

// ============================================================================
// Constants
// ============================================================================

const STANDARD_WORK_WEEK_HOURS = 40;

const ICON_SHAPES = [
  StarShape,
  MoonShape,
  CircleShape,
  Flower1Shape,
  Flower2Shape,
] as const;

// ============================================================================
// Helpers
// ============================================================================

interface EmploymentCardState {
  employment: Employment;
  isExpanded: boolean;
  key: string;
}

const createEmptyEmployment = (): Employment => ({
  id: crypto.randomUUID(),
  employerName: "",
  jobTitle: "",
  isCurrent: true,
  employmentType: DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed,
  incomes: [],
});

const isEmploymentDraft = (emp: Employment): boolean =>
  !emp.employerName ||
  !emp.jobTitle ||
  !emp.startMonth ||
  !emp.startYear ||
  emp.incomes.length === 0;

const getBaseIncome = (emp: Employment): EmploymentIncome | undefined =>
  emp.incomes.find((inc) => inc.incomeType === DomainIncomeType.IncomeBase);

/**
 * Convert an income amount to a monthly value based on its frequency.
 */
const toMonthly = (amount: number, frequency: DomainFrequencyType): number => {
  switch (frequency) {
    case DomainFrequencyType.FrequencyAnnual:
      return amount / 12;
    case DomainFrequencyType.FrequencyMonthly:
      return amount;
    case DomainFrequencyType.FrequencyBiWeekly:
      return amount * 2;
    case DomainFrequencyType.FrequencyWeekly:
      return (amount * 52) / 12;
    case DomainFrequencyType.FrequencyHourly:
      return 0; // Hourly base is calculated from rate × hours
    default:
      return amount;
  }
};

/**
 * Calculate the total monthly income for an employment record.
 */
const calculateMonthlyTotal = (emp: Employment): number => {
  let total = 0;
  for (const inc of emp.incomes) {
    const amt = inc.amount ?? 0;
    if (inc.incomeType === DomainIncomeType.IncomeBase) {
      if (inc.frequency === DomainFrequencyType.FrequencyHourly) {
        const hours = Math.min(inc.weeklyHours ?? 0, STANDARD_WORK_WEEK_HOURS);
        total += (amt * hours * 52) / 12;
      } else {
        total += toMonthly(amt, inc.frequency);
      }
    } else if (inc.incomeType === DomainIncomeType.IncomeOvertime) {
      const overtimeRate = inc.amount ?? 0;
      const overtimeHours = inc.overtimeHours ?? 0;
      total += (overtimeRate * overtimeHours * 52) / 12;
    } else {
      total += toMonthly(amt, inc.frequency);
    }
  }
  return total;
};

const formatCurrency = (n: number): string =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

/**
 * Count current employments excluding a specific index.
 * Used to determine if the "Current" toggle should be disabled.
 */
const countCurrentEmployments = (
  employments: Employment[],
  excludeIndex: number,
): number => {
  return employments.filter((emp, i) => i !== excludeIndex && emp.isCurrent)
    .length;
};

// ============================================================================
// Props
// ============================================================================

interface EmploymentHistoryFormProps {
  isCoBorrower?: boolean;
}

// ============================================================================
// Summary Card
// ============================================================================

interface EmploymentSummaryCardProps {
  employment: Employment;
  index: number;
  hasErrors?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const EmploymentSummaryCard: React.FC<EmploymentSummaryCardProps> = ({
  employment: emp,
  index,
  hasErrors = false,
  onEdit,
  onDelete,
}) => {
  const ShapeComponent = ICON_SHAPES[index % ICON_SHAPES.length];
  const isCurrent = emp.isCurrent;
  const isDraft = isEmploymentDraft(emp);
  const isUnknown = !emp.employerName;
  const baseIncome = getBaseIncome(emp);
  const monthlyTotal = calculateMonthlyTotal(emp);

  // Determine display income and frequency label
  const displayIncome =
    baseIncome?.frequency === DomainFrequencyType.FrequencyAnnual
      ? (baseIncome.amount ?? 0)
      : baseIncome?.frequency === DomainFrequencyType.FrequencyHourly
        ? monthlyTotal * 12
        : monthlyTotal;
  const freqLabel =
    baseIncome?.frequency === DomainFrequencyType.FrequencyAnnual ||
    baseIncome?.frequency === DomainFrequencyType.FrequencyHourly
      ? "ann"
      : "mo";

  const dateRange = formatDateRange(
    emp.startMonth,
    emp.startYear,
    emp.endMonth,
    emp.endYear,
    isCurrent,
  );

  return (
    <FormTableBody
      className={[
        "!p-4 transition-all duration-200 cursor-pointer hover:bg-[#f0f2ff] hover:!rounded-[16px]",
        isUnknown ? "bg-amber-50" : undefined,
        hasErrors
          ? "shadow-[inset_0_0_0_2px_#dc2626] !rounded-[16px]"
          : isCurrent
            ? "shadow-[inset_0_0_0_2px_#3265ea] !rounded-[16px]"
            : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex gap-4 items-center">
        {/* Icon with shape */}
        <div className="shrink-0">
          <div className="relative w-8 h-8">
            <ShapeComponent
              size={32}
              className={
                isUnknown
                  ? "text-amber-200"
                  : isCurrent
                    ? "text-[#e5ebfd]"
                    : "text-[#f0f1f4]"
              }
            />
            <div className="flex absolute inset-0 justify-center items-center">
              {isUnknown ? (
                <Alert01Icon
                  variant="bulk-rounded"
                  size={16}
                  className="text-amber-500"
                />
              ) : (
                <WorkHistoryIcon
                  variant="bulk-rounded"
                  size={16}
                  className={isCurrent ? "text-primary" : "text-[#687083]"}
                />
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex gap-1 items-center">
            <span className="text-base font-medium text-[#111827] truncate">
              {emp.employerName || "Employer Unknown"}
            </span>
            {isCurrent && (
              <span className="inline-flex items-center h-[15px] px-1 rounded-[5px] bg-[#e5ebfd] text-primary text-[10px] font-semibold shrink-0">
                Current
              </span>
            )}
            {isDraft && (
              <span className="inline-flex items-center h-[15px] px-1 rounded-[5px] bg-amber-100 text-amber-700 text-[10px] font-semibold shrink-0">
                Draft
              </span>
            )}
          </div>
          {dateRange && (
            <p className="text-xs text-[#4a5466] truncate">{dateRange}</p>
          )}
        </div>

        {/* Right: income + menu */}
        <div className="flex gap-2 items-center shrink-0">
          {displayIncome > 0 && (
            <div className="text-right">
              <p className="text-base font-semibold text-[#111827]">
                ${formatCurrency(displayIncome)}
              </p>
              <p className="text-[10px] text-[#31394a]">{freqLabel}</p>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="rounded-full"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit02Icon size={16} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-red-600 hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600"
              >
                <Delete03Icon size={16} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </FormTableBody>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const EmploymentHistoryForm: React.FC<EmploymentHistoryFormProps> = ({
  isCoBorrower = false,
}) => {
  const keyCounterRef = React.useRef(0);
  const generateKey = () => `emp-${++keyCounterRef.current}`;

  const store = useApplicationFormStore();
  const borrowerType = isCoBorrower ? "co" : "primary";
  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const [empCards, setEmpCards] = React.useState<EmploymentCardState[]>([]);
  const [showHistoryTooltip, setShowHistoryTooltip] = React.useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    React.useState(false);
  const [pendingNewStatus, setPendingNewStatus] =
    React.useState<DomainBorrowerEmploymentStatus | null>(null);

  // Sync from store on mount or when store data becomes available
  const storeEmploymentCount = borrower?.employmentHistory?.length ?? 0;
  React.useEffect(() => {
    if (!borrower) return;
    const storeEmployments = borrower.employmentHistory ?? [];
    if (storeEmployments.length > 0 && empCards.length === 0) {
      setEmpCards(
        storeEmployments.map((emp) => ({
          employment: emp,
          isExpanded: false,
          key: generateKey(),
        })),
      );
    }
  }, [storeEmploymentCount]);

  if (!borrower) return null;

  const googlePlacesApiKey = __GOOGLE_PLACES_API_KEY__;
  const employments = borrower.employmentHistory ?? [];

  // Keep cards in sync with store length changes (add/delete)
  React.useEffect(() => {
    if (empCards.length !== employments.length) {
      setEmpCards(
        employments.map((emp, i) => {
          const existing = empCards[i];
          return existing
            ? { ...existing, employment: emp }
            : { employment: emp, isExpanded: true, key: generateKey() };
        }),
      );
    }
  }, [employments.length]);

  React.useEffect(() => {
    setShowHistoryTooltip(
      (borrower &&
        borrower.employmentHistory.length > 0 &&
        !borrower.hasSufficientEmploymentHistory) ||
        false,
    );
  }, [borrower.hasSufficientEmploymentHistory]);

  // --- Handlers ---

  const handleAddEmployment = () => {
    const sectionErrorKey = `${borrowerPrefix}.employmentStatus`;
    if (store.fieldErrors[sectionErrorKey]) {
      store.clearFieldError(sectionErrorKey);
    }

    const newEmp = createEmptyEmployment();
    newEmp.isCurrent = employments.length === 0;
    store.addEmployment(borrowerType, newEmp);
    setEmpCards((prev) => [
      ...prev.map((c) => ({ ...c, isExpanded: false })),
      { employment: newEmp, isExpanded: true, key: generateKey() },
    ]);
  };

  const handleEdit = (index: number) => {
    setEmpCards((prev) =>
      prev.map((c, i) => ({ ...c, isExpanded: i === index })),
    );
  };

  const handleEmploymentChange = (
    index: number,
    patch: Partial<Employment>,
  ) => {
    const prefix = `${borrowerPrefix}.employmentHistory.${index}`;

    for (const [field, value] of Object.entries(patch)) {
      if (field === "employerAddress" && value && typeof value === "object") {
        for (const [addrField, addrValue] of Object.entries(value as Address)) {
          if (typeof addrValue === "string") {
            const errorKey = `${prefix}.employerAddress.${addrField}`;
            if (
              store.fieldErrors[errorKey] &&
              !validateAddressField(addrField, addrValue)
            ) {
              store.clearFieldError(errorKey);
            }
          }
        }
      } else if (field === "incomes" && Array.isArray(value)) {
        clearValidEmploymentIncomeFieldErrors(
          value,
          prefix,
          store.fieldErrors,
          store.clearFieldError,
        );
      } else if (typeof value === "string") {
        const errorKey = `${prefix}.${field}`;
        if (
          store.fieldErrors[errorKey] &&
          !validateEmploymentField(field, value)
        ) {
          store.clearFieldError(errorKey);
        }
      }
    }

    store.updateEmployment(borrowerType, index, patch);
  };

  const handleDeleteEmployment = (index: number) => {
    store.removeEmployment(borrowerType, index);
    setEmpCards((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Error computation ---

  const getErrors = (index: number): Record<string, string> => {
    const prefix = `${borrowerPrefix}.employmentHistory.${index}.`;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(prefix)) {
        result[k.slice(prefix.length)] = v;
      }
    }
    return result;
  };

  const getEmployerAddressErrors = (index: number): Record<string, string> => {
    const prefix = `${borrowerPrefix}.employmentHistory.${index}.employerAddress.`;
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(prefix)) {
        result[k.slice(prefix.length)] = v;
      }
    }
    return result;
  };

  // --- Render Expanded Form ---

  const renderExpandedForm = (emp: Employment, index: number) => (
    <FormTableBody key={empCards[index]?.key ?? `emp-${index}`}>
      <EmploymentFields
        value={emp}
        errors={getErrors(index)}
        employerAddressErrors={getEmployerAddressErrors(index)}
        showGooglePlaces
        googlePlacesApiKey={googlePlacesApiKey}
        showHistoryTooltip={showHistoryTooltip && index === 0}
        onDismissHistoryTooltip={() => setShowHistoryTooltip(false)}
        hasSufficientEmploymentHistory={borrower.hasSufficientEmploymentHistory}
        currentEmploymentCountExcludingSelf={countCurrentEmployments(
          employments,
          index,
        )}
        showDeleteButton
        onDelete={() => handleDeleteEmployment(index)}
        onChange={(patch) => handleEmploymentChange(index, patch)}
      />
    </FormTableBody>
  );

  // --- Main Render ---

  const updateBorrower = isCoBorrower
    ? store.updateCoBorrower
    : store.updatePrimaryBorrower;

  const employmentStatusError =
    store.fieldErrors[`${borrowerPrefix}.employmentStatus`];

  const isEmployed =
    borrower.employmentStatus ===
    DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed;
  const showAddButton = !borrower.employmentStatus || isEmployed;

  return (
    <div className="space-y-3">
      {/* Employment status radio group */}
      <FormTable
        title="Employment Status"
        headerIcon={
          <WorkHistoryIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#6F41E6]"
          />
        }
      >
        <FormTableBody>
          <div className="px-4 py-3">
            <RadioGroup
              value={borrower.employmentStatus ?? ""}
              onValueChange={(v) => {
                if (store.fieldErrors[`${borrowerPrefix}.employmentStatus`]) {
                  store.clearFieldError(`${borrowerPrefix}.employmentStatus`);
                }

                const oldStatus = borrower.employmentStatus;
                const newStatus = v as DomainBorrowerEmploymentStatus;

                // Check if switching from Employed to non-Employed with existing records
                if (
                  oldStatus ===
                    DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed &&
                  newStatus !==
                    DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed &&
                  (borrower.employmentHistory?.length ?? 0) > 0
                ) {
                  // Show confirmation dialog
                  setPendingNewStatus(newStatus);
                  setShowDeleteConfirmation(true);
                  return; // Don't update status yet
                }

                // Direct update if no confirmation needed
                updateBorrower({ employmentStatus: newStatus });

                // Delete records if switching from Employed to non-Employed without existing records
                if (
                  oldStatus ===
                    DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed &&
                  newStatus !==
                    DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed
                ) {
                  const employments = borrower.employmentHistory ?? [];
                  employments.forEach((emp, index) => {
                    if (emp.serverId) {
                      store.removeEmployment(borrowerType, index);
                    }
                  });
                }
              }}
              className="flex flex-wrap gap-2"
            >
              <RadioGroupCardItem
                value={
                  DomainBorrowerEmploymentStatus.BorrowerEmploymentEmployed
                }
                label="Employed"
                variant="inline"
              />
              <RadioGroupCardItem
                value={
                  DomainBorrowerEmploymentStatus.BorrowerEmploymentUnemployed
                }
                label="Unemployed"
                variant="inline"
              />
              <RadioGroupCardItem
                value={DomainBorrowerEmploymentStatus.BorrowerEmploymentRetired}
                label="Retired"
                variant="inline"
              />
            </RadioGroup>
            {employmentStatusError && (
              <p className="text-sm text-[#872525] mt-1">
                {employmentStatusError}
              </p>
            )}
          </div>
        </FormTableBody>
      </FormTable>

      {isEmployed && (
        <FormTable
          title="Employment History"
          headerIcon={
            <WorkHistoryIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
          style={containerStyles.style}
        >
          {empCards.map((card, index) => {
            const errorPrefix = `${borrowerPrefix}.employmentHistory.${index}.`;
            const hasErrors = Object.keys(store.fieldErrors).some((k) =>
              k.startsWith(errorPrefix),
            );

            return card.isExpanded ? (
              renderExpandedForm(employments[index], index)
            ) : (
              <EmploymentSummaryCard
                key={card.key}
                employment={employments[index]}
                index={index}
                hasErrors={hasErrors}
                onEdit={() => handleEdit(index)}
                onDelete={() => handleDeleteEmployment(index)}
              />
            );
          })}

          {showAddButton && (
            <FormTableAddButton
              onClick={handleAddEmployment}
              {...addButtonState.interactionProps}
            >
              <Add01Icon variant="stroke-rounded" size={12} />
              {employments.length === 0 ? "Add history" : "Additional history"}
            </FormTableAddButton>
          )}
        </FormTable>
      )}
      {showDeleteConfirmation ? (
        <Dialog
          open={showDeleteConfirmation}
          onOpenChange={(open) => {
            if (!open) {
              setShowDeleteConfirmation(false);
              setPendingNewStatus(null);
            }
          }}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <div className="flex gap-2 items-center">
                <Alert01Icon size={18} className="text-red-600" />
                <DialogTitle>Delete Employment History?</DialogTitle>
              </div>
            </DialogHeader>
            <DialogBody>
              <p className="text-base text-[#4a5466]">
                You have {borrower.employmentHistory?.length} employment
                record(s). Changing to non-Employed status will delete all
                employment history. This cannot be undone.
              </p>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="secondary"
                size="s"
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setPendingNewStatus(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-secondary"
                size="s"
                onClick={() => {
                  const employments = borrower.employmentHistory ?? [];
                  for (let i = employments.length - 1; i >= 0; i--) {
                    if (employments[i].serverId) {
                      store.removeEmployment(borrowerType, i);
                    }
                  }
                  updateBorrower({
                    employmentStatus:
                      pendingNewStatus as DomainBorrowerEmploymentStatus,
                  });
                  setShowDeleteConfirmation(false);
                  setPendingNewStatus(null);
                }}
              >
                Yes, delete all
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

export default EmploymentHistoryForm;
